import { useMemo } from 'react'
import Calculation from '../../../../../types/api/calculation'
import { DeepPartial } from '../../../../../types/utility-types'
import FormValues, { ListItem } from '../types/form-values'
import CalculationPosition from '../../../../../types/api/calculation-position'
import Shipment from '../../../../../types/api/shipment'

export default function useInitialValues(calc: Calculation) {
    const initialValues = useMemo(() => {
        // TODO: для сборок нужно будет сначала сгруппировать
        // const groups = calc.calculation_positions.re

        const positions = calc.calculation_positions || []

        const groupedPositions = getGroupedPositions(positions)

        const list = groupedPositions.map(item => {

            const calcPos = item[0]
            const shipPos = calcPos.shipment_position
            const ship = calcPos.shipment_position?.shipment
            const hasPreviousBlankPreview = Boolean(ship?.blankPreviewFiles?.length)
            const hasFlipJob = (calcPos.additional_jobs || []).some(job => {
                const name = (job.name || '').toLowerCase()
                return name === 'переворот' || name.includes('переворот')
            })

            const positions = getPositions(item)

            const getPropOrUndefined = <T extends keyof Shipment>(prop: T) =>
                ship && prop in ship ? ship[prop] : undefined

            const listItem: DeepPartial<ListItem> = {
                positions: positions,
                designer: ship?.designer?.documentId || null,
                windingSchemeFace: ship?.windingSchemeFace,
                // Без Л/О в форме намотку оборота не подставляем — иначе disabled-поле уедет в create
                windingSchemeTurn: undefined,
                quantityPerRoll: calcPos.quantityPerRoll, // TODO: тут аккуратно, если намотка меняется, то сборка может развалиться
                package: ship?.package || undefined,
                manualLabeling: getPropOrUndefined('manualLabeling'),
                clientLabel: getPropOrUndefined('clientLabel'),
                packageCopies: ship?.packageCopies,
                pallet: ship?.pallet,
                congreve: getPropOrUndefined('congreve'),
                priming: getPropOrUndefined('priming'),
                comment: shipPos?.comment || '',
                calculationPosition: calcPos,
                bushing: ship?.bushing || undefined,
                // Если в доп. работах позиции расчета нет "Переворот", значение "печать на клеевом слое"
                // должно быть невыбираемым и предопределенным.
                printingOnGlueLayer: hasFlipJob
                    ? getPropOrUndefined('printingOnGlueLayer')
                    : (ship?.printingOnGlueLayer ?? false),
                reusePreviousBlankPreview: hasPreviousBlankPreview,
                lacquers: calcPos.lacquers?.length
                    ? calcPos.lacquers.map(l => ({
                          type: l.type?.documentId ?? null,
                          name: l.name || '',
                      }))
                    : Array(calcPos.varnish || 0).fill({ type: null, name: '' }),
                windingInfo: ship?.windingInfo || '',
                materialRequirements: {
                    materialTypeDocId: calcPos.material_type?.documentId,
                    materialTypeName: calcPos.material_type?.name,
                    materialInCutting: calcPos.material_type?.materialInCutting,
                    configurations: [{}],
                },
                additionalMaterialRequirements: calcPos.additional_material_types?.map(x => ({
                    materialTypeDocId: x.documentId,
                    materialTypeName: x.name,
                    materialInCutting: x.materialInCutting,
                    configurations: [{}],
                })),
            }

            return listItem
        })

        const values: DeepPartial<FormValues> = {
            list,
        }

        return values
    }, [calc])

    return initialValues
}

function getPositions(item: CalculationPosition[]) {
    const result: Array<{
        name: string
        copies?: number
        comment?: string
        floodsInAccumulation?: number
    }> = []

    item.forEach(x => {
        if (x.numberOfVariantsForDigit) {
            for (let i = 0; i < x.numberOfVariantsForDigit; i++) {
                result.push({
                    name: '',
                    copies: 0,
                    comment: '',
                    floodsInAccumulation: x.floodsInAccumulation,
                })
            }
        } else {
            result.push({
                name: x.shipment_position?.labelName ?? '',
                copies: x.copies,
                comment: x.comment,
                floodsInAccumulation: x.floodsInAccumulation,
            })
        }
    })

    return result
}

function getGroupedPositions(positions: CalculationPosition[]) {
    return positions.reduce((arr: CalculationPosition[][], pos) => {
        if (pos.accumulation) {
            const group = arr.find(x => x.find(y => y.accumulation === pos.accumulation))
            if (group) {
                group.push(pos)
            } else {
                arr.push([pos])
            }
        } else {
            arr.push([pos])
        }

        return arr
    }, [])
}
